/**
 * ColourMatch Pro · Business Profile · Public Profile tab.
 *
 * Shows the current public URL, a preview link and copy button. The
 * public listing is framed as an OPTIONAL membership benefit — not the
 * focus of ColourMatch Pro.
 */
import { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Copy, Check, Info } from "lucide-react";

export default function PublicProfile() {
  const { me } = useOutletContext();
  const slug = me?.slug;
  const publicUrl = slug ? `${window.location.origin}/painters/${slug}` : null;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press to copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="bp-public-profile">
      <div className="rounded-2xl border border-brand-linen bg-white p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div>
            <div className="font-semibold text-brand-ink">A benefit of ColourMatch Pro membership</div>
            <p className="text-sm text-brand-muted mt-1.5 leading-relaxed max-w-2xl">
              Homeowners on ColourMatch can discover your business through the public listing.
              This is one benefit of your subscription — not the purpose of ColourMatch Pro.
            </p>
          </div>
        </div>

        {publicUrl ? (
          <div className="mt-4 pt-4 border-t border-brand-linen">
            <div className="text-xs text-brand-muted uppercase tracking-widest mb-2">Your public URL</div>
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:underline break-all"
                data-testid="public-url-link"
              >
                {publicUrl}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs border border-brand-linen hover:border-brand-teal text-brand-ink transition-colors"
                data-testid="public-url-copy"
                type="button"
              >
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <div className="mt-5">
              <Link
                to={`/painters/${slug}`}
                className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors"
                data-testid="public-preview-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                Preview public listing <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-brand-linen text-sm text-brand-muted">
            Your public listing will appear here once your ColourMatch Pro subscription is active.
          </div>
        )}
      </div>
    </div>
  );
}
